import bowser from 'bowser'
import { intl } from 'lang/intl'
import React, { useCallback } from 'react'
import { useModal } from '../../../Layout/Modal'
import TtFormik from '../../../global/formik-forms'
import {
  useDevices,
  useHasGrantedDevicesPermission,
  useSelectedDevices,
} from '../../useDevices'

import { VideoPreview } from './VideoPreview/VideoPreview'
import { AudioPreview } from './AudioPreview/AudioPreview'

import './DeviceSelector.scss'

type FormValues = {
  videoinput: string | ''
  audioinput: string | ''
  audiooutput: string | ''
}

interface DeviceSelectorProps {
  onSubmit: (values: FormValues) => void
}

const DeviceSelector = ({ onSubmit }: DeviceSelectorProps) => {
  const { closeModal } = useModal()
  const devicesState = useSelectedDevices()
  const devices = useDevices()
  const hasMediaPermissions = useHasGrantedDevicesPermission()

  const isAudioOutputSupported = !bowser.safari && !bowser.mobile

  return (
    <div className="device-selector-modal">
      {!hasMediaPermissions && (
        <div className="device-selector-permissions-note">
          {intl.formatMessage({
            defaultMessage:
              'Please grant microphone and camera access to view the list of devices.',
          })}
        </div>
      )}
      <TtFormik.Form<FormValues>
        className="device-selector"
        label="Device Selector"
        initialValues={{
          videoinput: devicesState.videoinput ?? '',
          audioinput: devicesState.audioinput ?? '',
          audiooutput: devicesState.audiooutput ?? '',
        }}
        enableReinitialize
        onSubmit={(formValues) => {
          onSubmit(formValues)
          closeModal()
        }}
      >
        {(formik) => (
          <>
            <div className="device-selector-preview">
              <VideoPreview deviceId={formik.values.videoinput} />
              <AudioPreview deviceId={formik.values.audioinput} />
            </div>
            <div className="device-selector-form">
              <TtFormik.Select<FormValues>
                name="videoinput"
                label="Camera"
                hoist
              >
                <TtFormik.Option<FormValues>
                  name="videoinput"
                  value=""
                  label="No device selected"
                />
                {devices.videoinput.map((d) => (
                  <TtFormik.Option<FormValues>
                    key={d.deviceId}
                    name="videoinput"
                    value={d.deviceId}
                    label={d.label}
                  />
                ))}
              </TtFormik.Select>
              <TtFormik.Select<FormValues>
                name="audioinput"
                label="Microphone"
                hoist
              >
                <TtFormik.Option<FormValues>
                  name="audioinput"
                  value=""
                  label="No device selected"
                />
                {devices.audioinput.map((d) => (
                  <TtFormik.Option<FormValues>
                    key={d.deviceId}
                    name="audioinput"
                    value={d.deviceId}
                    label={d.label}
                  />
                ))}
              </TtFormik.Select>
              {isAudioOutputSupported && (
                <TtFormik.Select<FormValues>
                  name="audiooutput"
                  label="Speaker and headphones"
                  hoist
                >
                  <TtFormik.Option<FormValues>
                    name="audiooutput"
                    value=""
                    label="No device selected"
                  />
                  {devices.audiooutput.map((d) => (
                    <TtFormik.Option<FormValues>
                      key={d.deviceId}
                      name="audiooutput"
                      value={d.deviceId}
                      label={d.label}
                    />
                  ))}
                </TtFormik.Select>
              )}
              <TtFormik.SubmitButton
                className="device-selector-form-submit"
                disabled={false}
                pill
                onCancel={closeModal}
              >
                Choose devices
              </TtFormik.SubmitButton>
            </div>
          </>
        )}
      </TtFormik.Form>
    </div>
  )
}

const useOpenDeviceSelector = () => {
  const { openModal } = useModal()

  return useCallback(() => {
    return new Promise<FormValues>((resolve) => {
      openModal({
        body: <DeviceSelector onSubmit={resolve} />,
        label: intl.formatMessage({ defaultMessage: 'Change devices' }),
        closable: false,
      })
    })
  }, [openModal])
}

export { useOpenDeviceSelector }
